import {NgModule} from '@angular/core';
import {RouterModule, Routes} from '@angular/router';

import {ContentComponent} from './layout/content/content.component';
import {FocusComponent} from './layout/focus/focus.component';
import {LayoutModule} from './layout/layout.module';
import {ForbiddenComponent} from './views/forbidden/forbidden.component';
import {ECNotificationsComponent} from './views/ec-notifications/ec-notifications.component';
import {ECMembersComponent} from './views/ec-members/ec-members.component';
import {ConfigConnectivityComponent} from './views/connectivity/config-connectivity.component';
import {PermAdminPanelComponent} from './views/perm-admin/perm-admin-panel.component';
import {PermAdminResourcesComponent} from './views/perm-admin/perm-admin-resources/perm-admin-resources.component';
import {PermAdminConnectorsComponent} from './views/perm-admin/perm-admin-connectors/perm-admin-connectors.component';
import {PermAdminUsersComponent} from './views/perm-admin/perm-admin-users/perm-admin-users.component';
import {
    UsersPermissionAssignmentsComponent
} from './views/perm-admin/users-permission-assignments/users-permission-assignments.component';
import {ECEconomicsComponent} from './views/ec-economics/ec-economics.component';
import {I18nModule} from './i18n/i18n.module';
import {ECResolverService} from './views/ec-container/ec-resolver.service';
import {
    ConfigurationConnectivityResolverService
} from './views/connectivity/configuration-connectivity-resolver.service';
import {
    PermAdminResourcesResolverService
} from './views/perm-admin/perm-admin-resources/perm-admin-resources-resolver.service';
import {
    PermAdminConnectorsResolverService
} from './views/perm-admin/perm-admin-connectors/perm-admin-connectors-resolver.service';
import {PermAdminUsersResolverService} from './views/perm-admin/perm-admin-users/perm-admin-users-resolver.service';
import {
    UsersPermissionAssignmentsResolverService
} from './views/perm-admin/users-permission-assignments/users-permission-assignments-resolver.service';
import {
    ECEconomicsAggregatedComponent
} from './views/ec-economics/economics-aggregated/ec-economics-aggregated.component';
import {
    ECEconomicsRedistributedComponent
} from './views/ec-economics/economics-redistributed/ec-economics-redistributed.component';
import {AcceptClausesGuard, ActivateTotpGuard, ApprovedGuard, AuthGuard} from 'auth-commons';
import {UserAdminPanelComponent} from './views/user-admin-panel/user-admin-panel.component';
import {UserProfileComponent} from './views/user-profile/user-profile.component';
import {AuthenticationResolverService} from './views/authentication-resolver.service';
import {LoginComponent} from './views/login/login.component';
import {ChangePwdComponent} from './views/change-pwd/change-pwd.component';
import {AcceptClausesComponent} from './views/accept-clauses/accept-clauses.component';
import {ActivateTotpComponent} from './views/activate-totp/activate-totp.component';
import {RecoverPwdComponent} from './views/recover-pwd/recover-pwd.component';
import {ECPlantsComponent} from './views/ec-economics/plants/ec-plants.component';
import {ECMarketComponent} from './views/ec-economics/market/ec-market.component';
import {DevDashboardComponent} from './views/dev-dashboard/dev-dashboard.component';
import {DevDashboardResolverService} from './views/dev-dashboard/dev-dashboard-resolver.service';
import {CustomerCareComponent} from './views/customer-care/customer-care.component';
import {CustomerCareResolverService} from './views/customer-care/customer-care-resolver.service';
import {ECContainerComponent} from './views/ec-container/ec-container.component';
import {ECDashboardComponent} from './views/ec-dashboard/ec-dashboard.component';
import {ECMeasuresComponent} from './views/ec-measures/ec-measures.component';
import {ECWeatherForecastComponent} from './views/ec-weather-forecast/ec-weather-forecast.component';
import {ECResourcesComponent} from './views/ec-resources/ec-resources.component';
import {ECPlanningComponent} from './views/ec-planning/ec-planning.component';
import {ForecastComponent} from './views/forecast/forecast.component';
import {ECRepresentativesComponent} from './views/ec-representatives/ec-representatives.component';
import {ECRepresentativesResolverService} from './views/ec-representatives/ec-representatives-resolver.service';
import {EMSScopeGuard} from './ems-scope.guard';
import {ECDocumentsComponent} from './views/ec-documents/ec-documents.component';
import {AdvancedUserGuard} from './advanced-user.guard';
import {EcEconomicsSubjectsSummaryComponent} from './views/ec-economics/economics-subjects-summary/ec-economics-subjects-summary.component';
import {
    EcEconomicsTotalsSummaryComponent
} from './views/ec-economics/economics-totals-summary/ec-economics-totals-summary.component';
import {
    EconomicsTotalsPlantsComponent
} from './views/ec-economics/economics-totals-plants/economics-totals-plants.component';
import {EconomicsGuard} from './economics.guard';
import {
    EconomicsMemberPlantsComponent
} from './views/ec-economics/economics-member-plants/economics-member-plants.component';
import {
    EconomicsTotalsCommunityComponent
} from './views/ec-economics/economics-totals-community/economics-totals-community.component';
import {EconomicsMemberPodsComponent} from './views/ec-economics/economics-member-pods/economics-member-pods.component';

const routes: Routes = [
    {
        path: 'c',
        component: ContentComponent,
        children: [
            {
                path: 'login',
                component: LoginComponent,
                data: {
                    title: 'Login'
                },
                resolve: {_dummy: AuthenticationResolverService}
            },
            {
                path: 'recover-password',
                component: RecoverPwdComponent,
                data: {
                    title: 'Password recovery'
                },
                resolve: {_dummy: AuthenticationResolverService}
            },
            {
                path: 'change-password',
                component: ChangePwdComponent,
                canActivate: [AuthGuard],
                data: {
                    title: 'Change password'
                },
                resolve: {_dummy: AuthenticationResolverService}
            },
            {
                path: 'accept-clauses',
                component: AcceptClausesComponent,
                canActivate: [AuthGuard, AcceptClausesGuard],
                data: {
                    title: 'Accept clauses'
                },
                resolve: {_dummy: AuthenticationResolverService}
            },
            {
                path: 'activate-totp',
                component: ActivateTotpComponent,
                canActivate: [AuthGuard, ActivateTotpGuard],
                data: {
                    title: 'Activate 2FA'
                }
            },
            {
                path: 'change-user-profile',
                component: UserProfileComponent,
                canActivate: [AuthGuard, ApprovedGuard],
                data: {
                    title: 'Profile'
                },
                resolve: {_dummy: AuthenticationResolverService}
            },
            {
                path: 'user-admin-panel',
                component: UserAdminPanelComponent,
                canActivate: [AuthGuard, ApprovedGuard, AdvancedUserGuard],
                data: {
                    title: 'User administration'
                },
                resolve: {_dummy: AuthenticationResolverService}
            },
            {
                path: 'connectivity',
                component: ConfigConnectivityComponent,
                canActivate: [AuthGuard, ApprovedGuard, AdvancedUserGuard],
                data: {
                    title: 'Connectivity'
                },
                resolve: {_dummy: ConfigurationConnectivityResolverService}
            },
            {
                path: 'permissions',
                component: PermAdminPanelComponent,
                canActivate: [AuthGuard, ApprovedGuard, AdvancedUserGuard],
                canActivateChild: [AuthGuard, ApprovedGuard, AdvancedUserGuard],
                children: [
                    {
                        path: 'resources',
                        component: PermAdminResourcesComponent,
                        data: {
                            title: 'Permissions management'
                        },
                        resolve: {_dummy: PermAdminResourcesResolverService}
                    },
                    {
                        path: 'connectors',
                        component: PermAdminConnectorsComponent,
                        data: {
                            title: 'Connector permissions'
                        },
                        resolve: {_dummy: PermAdminConnectorsResolverService}
                    },
                    {
                        path: 'users',
                        component: PermAdminUsersComponent,
                        data: {
                            title: 'User permissions'
                        },
                        resolve: {_dummy: PermAdminUsersResolverService}
                    },
                    {
                        path: 'users-assignments',
                        component: UsersPermissionAssignmentsComponent,
                        data: {
                            title: 'Users permissions assignments'
                        },
                        resolve: {_dummy: UsersPermissionAssignmentsResolverService}
                    },
                    {
                        path: '',
                        redirectTo: 'resources',
                        pathMatch: 'full'
                    }
                ]
            },
            {
                path: 'dev-dashboard',
                component: DevDashboardComponent,
                data: {
                    title: 'Supervision dashboard'
                },
                canActivate: [AuthGuard, ApprovedGuard, AdvancedUserGuard],
                resolve: {_dummy: DevDashboardResolverService}
            },
            {
                path: 'ec',
                component: ECContainerComponent,
                data: {
                    title: 'Energy Community'
                },
                canActivate: [AuthGuard, ApprovedGuard],
                canActivateChild: [AuthGuard, ApprovedGuard],
                resolve: {_dummy: ECResolverService},
                children: [
                    {
                        path: 'dashboard',
                        component: ECDashboardComponent,
                        data: {
                            title: 'Dashboard'
                        }
                    },
                    {
                        path: 'resources',
                        component: ECResourcesComponent,
                        data: {
                            title: 'Resources'
                        }
                    },
                    {
                        path: 'measures',
                        component: ECMeasuresComponent,
                        data: {
                            title: 'Measures'
                        }
                    },
                    {
                        path: 'weather-forecast',
                        component: ECWeatherForecastComponent,
                        data: {
                            title: 'Weather forecast'
                        }
                    },
                    {
                        path: 'documents',
                        component: ECDocumentsComponent,
                        canActivate: [AuthGuard, ApprovedGuard, AdvancedUserGuard],
                        data: {
                            title: 'Documents'
                        }
                    },
                    {
                        path: 'economics',
                        component: ECEconomicsComponent,
                        data: {
                            title: 'Economics'
                        },
                        canActivate: [AuthGuard, ApprovedGuard, EconomicsGuard],
                        canActivateChild: [AuthGuard, ApprovedGuard, EconomicsGuard],
                        children: [
                            {
                                path: 'aggregated',
                                component: ECEconomicsAggregatedComponent,
                                data: {
                                    title: 'Economics'
                                }
                            },
                            {
                                path: 'plants',
                                component: ECPlantsComponent,
                                data: {
                                    title: 'Plants'
                                }
                            },
                            {
                                path: 'market',
                                component: ECMarketComponent,
                                data: {
                                    title: 'Energy market indicators'
                                }
                            },
                            {
                                path: 'redistributed',
                                component: ECEconomicsRedistributedComponent,
                                data: {
                                    title: 'Economics'
                                }
                            },
                            {
                                path: 'subjects-summary',
                                component: EcEconomicsSubjectsSummaryComponent,
                                data: {
                                    title: 'Economics'
                                }
                            },
                            {
                                path: 'totals-summary',
                                component: EcEconomicsTotalsSummaryComponent,
                                data: {
                                    title: 'Economics'
                                }
                            },
                            {
                                path: 'totals-plants',
                                component: EconomicsTotalsPlantsComponent,
                                data: {
                                    title: 'Economics'
                                }
                            },
                            {
                                path: 'totals-community',
                                component: EconomicsTotalsCommunityComponent,
                                data: {
                                    title: 'Economics'
                                }
                            },
                            {
                                path: 'member-plants',
                                component: EconomicsMemberPlantsComponent,
                                data: {
                                    title: 'Economics'
                                }
                            },
                            {
                                path: 'member-pods',
                                component: EconomicsMemberPodsComponent,
                                data: {
                                    title: 'Economics'
                                }
                            }
                        ]
                    },
                    {
                        path: 'members',
                        component: ECMembersComponent,
                        canActivate: [AuthGuard, ApprovedGuard, AdvancedUserGuard],
                        data: {
                            title: 'Subjects'
                        }
                    },
                    {
                        path: 'notifications',
                        component: ECNotificationsComponent,
                        data: {
                            title: 'Notifications'
                        }
                    },
                    {
                        path: 'optimization/:planningId',
                        component: ECPlanningComponent,
                        data: {
                            title: 'Planning'
                        }
                    },
                    {
                        path: 'representatives',
                        component: ECRepresentativesComponent,
                        data: {
                            title: 'Representatives'
                        },
                        canActivate: [EMSScopeGuard],
                        resolve: {_dummy: ECRepresentativesResolverService}
                    },
                    {
                        path: '',
                        redirectTo: 'dashboard',
                        pathMatch: 'full'
                    }
                ]
            },
            {
                path: 'prediction/:forecastId',
                component: ForecastComponent,
                data: {
                    title: 'Forecast'
                }
            },
            {
                path: 'customer-care',
                component: CustomerCareComponent,
                data: {
                    title: 'Customer support'
                },
                canActivate: [AuthGuard, ApprovedGuard],
                resolve: {_dummy: CustomerCareResolverService}
            },
            {
                path: '',
                redirectTo: 'dev-dashboard',
                pathMatch: 'full'
            }
        ],
        resolve: {}
    },
    {
        path: 'f',
        component: FocusComponent,
        children: [
            {
                path: 'forbidden',
                component: ForbiddenComponent
            },
            {
                path: '',
                redirectTo: 'dev-dashboard',
                pathMatch: 'full'
            }
        ]
    },
    {
        path: '',
        pathMatch: 'full',
        redirectTo: 'c'
    },
    {
        path: '**',
        redirectTo: 'c'
    }
];

@NgModule({
    imports: [
        RouterModule.forRoot(routes),
        // app
        LayoutModule,
        I18nModule
    ],
    exports: [RouterModule]
})
export class AppRoutingModule {
}
